import { getBookingLocationLabel } from '@duda-co/booking/runtime';
import { localizationService } from '@shared-services/localization-service';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import { Icon } from 'client/widget-components/basicComponents/Icon/Icon';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Booking } from '../../types';
import {
    buildBookingTitle,
    getLocaleTimeZoneName,
    sanitizeMail,
} from '../../utils';
import CloseButton from '../CloseButton';
import { Divider, FooterNote, Modal, Title } from '../Modal';
import { bookingStrings, str } from './strings';

const confirmationStrings = bookingStrings.confirmation.success;

// The Booker's own label scope: a booking with no video room carries the raw Cal location type instead of a
// join link, and the label for it comes out of the same table the event meta above already uses
// (SNIPDUDE-7964).
const bookerStr = (key: string) =>
    localizationService.str(`ui.runtimessr.booker.${key}`);

const getDataAuto = (key: string) => `booking-modal-view-${key}`;

export enum BookingDataViewType {
    SUCCESS = 'success',
    // cancellation modal with reason
    CANCELLATION = 'cancellation',
    // result of cancellation - successful cancel modal
    CANCELED = 'canceled',
    PENDING = 'pending',
}

interface BookingDataViewProps {
    bookingData: Booking;
    title: string;
    subtitle?: string;
    children?: ReactNode;
    dataAuto?: string;
    onClose?: () => void;
    svgIcon?: ReactNode;
    type?: BookingDataViewType;
}

export const BookingDataView = ({
    bookingData,
    children,
    dataAuto,
    onClose,
    title,
    svgIcon,
    subtitle,
    type = BookingDataViewType.SUCCESS,
}: BookingDataViewProps) => {
    const { dmAPI } = getWindowInBrowser();
    const currentLocale = dmAPI?.getSiteCurrentLocale?.();
    const startDate = new Date(bookingData.startTime);
    const endDate = new Date(bookingData.endTime);

    const attendeeTimeZone = bookingData.attendees[0]?.timeZone;
    const timeZoneOption: Pick<Intl.DateTimeFormatOptions, 'timeZone'> =
        attendeeTimeZone ? { timeZone: attendeeTimeZone } : {};

    const datePart = startDate.toLocaleDateString(currentLocale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...timeZoneOption,
    });

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        ...timeZoneOption,
    };

    const startTime = startDate.toLocaleTimeString(currentLocale, timeOptions);
    const endTime = endDate.toLocaleTimeString(currentLocale, timeOptions);

    const timeZoneName = getLocaleTimeZoneName(
        startDate,
        currentLocale,
        timeOptions
    );

    return (
        <Modal dataAuto={dataAuto} disableBodyScroll>
            <BookingDataViewWrapper data-auto={getDataAuto(type)}>
                {onClose && (
                    <CloseButton
                        onClick={onClose}
                        data-auto={getDataAuto('close-button')}
                    />
                )}
                <Header>
                    {svgIcon && (
                        <IconWrapper
                            type={type}
                            data-type={getDataAuto(`${type}-icon`)}
                        >
                            {svgIcon}
                        </IconWrapper>
                    )}
                    <Title data-auto={getDataAuto('title')}>{title}</Title>
                    {subtitle && (
                        <Subtitle data-auto={getDataAuto('subtitle')}>
                            {subtitle}
                        </Subtitle>
                    )}
                </Header>
                <Divider />
                <Details>
                    <Detail>
                        <Label data-auto={getDataAuto('what-label')}>
                            {str(confirmationStrings.what)}:
                        </Label>
                        <Value data-auto={getDataAuto('what-value')}>
                            {buildBookingTitle(bookingData)}
                        </Value>
                    </Detail>
                    <Detail>
                        <Label data-auto={getDataAuto('when-label')}>
                            {str(confirmationStrings.when)}:
                        </Label>
                        <WhenContent
                            isCanceled={type === BookingDataViewType.CANCELED}
                        >
                            <Value data-auto={getDataAuto('when-value')}>
                                {datePart}
                            </Value>
                            <Value>{`${startTime} - ${endTime} ${timeZoneName}`}</Value>
                        </WhenContent>
                    </Detail>
                    <Detail>
                        <Label data-auto={getDataAuto('who-label')}>
                            {str(confirmationStrings.who)}:
                        </Label>
                        <Attendees>
                            <div>
                                <Value>
                                    {bookingData.hosts
                                        .map((host) => host.name)
                                        .join(', ')}
                                    <HostTag>
                                        {str(confirmationStrings.host)}
                                    </HostTag>
                                </Value>
                                <Value
                                    data-auto={getDataAuto('who-user-value')}
                                >
                                    {bookingData.hosts
                                        .map((host) => sanitizeMail(host.email))
                                        .join(', ')}
                                </Value>
                            </div>
                            {bookingData.attendees.map((attendee) => (
                                <div
                                    key={attendee.email}
                                    data-auto={getDataAuto(
                                        'who-attendees-value'
                                    )}
                                >
                                    <Value>{attendee.name}</Value>
                                    <Value>
                                        {sanitizeMail(attendee.email)}
                                    </Value>
                                </div>
                            ))}
                        </Attendees>
                    </Detail>
                    <Detail>
                        <Label data-auto={getDataAuto('where-label')}>
                            {str(confirmationStrings.where)}:
                        </Label>
                        {bookingData.videoCallUrl ? (
                            <Value data-auto={getDataAuto('where-value')}>
                                <CallLink
                                    href={bookingData.videoCallUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {str(confirmationStrings.video)}
                                    <Icon name='icon-external-link' />
                                </CallLink>
                            </Value>
                        ) : (
                            <Value data-auto={getDataAuto('where-value')}>
                                {getBookingLocationLabel(
                                    bookingData.location,
                                    bookerStr
                                )}
                            </Value>
                        )}
                    </Detail>
                    {bookingData.responses?.notes && (
                        <Detail>
                            <Label data-auto={getDataAuto('notes-label')}>
                                {str(confirmationStrings.notes)}:
                            </Label>
                            <Value data-auto={getDataAuto('notes-value')}>
                                {bookingData.responses.notes}
                            </Value>
                        </Detail>
                    )}
                </Details>
                {children && (
                    <>
                        <Divider />
                        <FooterNote>{children}</FooterNote>
                    </>
                )}
            </BookingDataViewWrapper>
        </Modal>
    );
};

export const calcomColors = {
    bgSuccess: '#e8fbee',
    textSuccess: '#60ac68',
    hostTagBg: '#D9E9FC',
    hostTagText: '#174983',
    bgCancelation: '#f5e3e2',
    textCancelation: '#ca3932',
    bgPending: '#DEE8FC',
    textPending: '#6786B1',
};

const BookingDataViewWrapper = styled.div`
    margin: 0 auto;
`;

const Subtitle = styled.div`
    font-weight: 350;
    font-size: 18px;
    line-height: 34px;
    color: #5a5a5a;
    margin-block-start: 4px;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    line-height: 30px;
`;

const Detail = styled.div`
    display: grid;
    grid-template-columns: 140px 1fr;
    a {
        display: flex;
        align-items: center;
        gap: 4px;
        text-decoration: none;
    }
`;

const Label = styled.span`
    font-weight: 500;
    font-size: 16px;
    color: #5d6068;
    text-align: start;
    width: 140px;
`;

const Attendees = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    > div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Value = styled.span`
    text-align: start;
    word-break: break-word;
    font-weight: 350;
    font-size: 16px;
`;

const CallLink = styled.a`
    color: inherit;
    text-decoration: none;
`;

const WhenContent = styled.span<{ isCanceled: boolean }>`
    text-align: start;
    display: flex;
    flex-direction: column;
    ${({ isCanceled }) =>
        isCanceled &&
        css`
            text-decoration: line-through;
        `}
`;

const HostTag = styled.span`
    background-color: ${calcomColors.hostTagBg};
    color: ${calcomColors.hostTagText};
    padding: 2px 6px;
    box-sizing: border-box;
    border-radius: 4px;
    margin-inline-start: 8px;
    font-size: 12px;
    font-weight: 500;
`;

const Header = styled.div`
    text-align: center;
    display: flex;
    flex-direction: column;
`;

const iconStyles = {
    [BookingDataViewType.SUCCESS]: {
        bg: calcomColors.bgSuccess,
        text: calcomColors.textSuccess,
        stroke: calcomColors.textSuccess,
    },
    [BookingDataViewType.CANCELLATION]: {
        bg: calcomColors.bgCancelation,
        text: calcomColors.textCancelation,
        stroke: calcomColors.textCancelation,
    },
    [BookingDataViewType.CANCELED]: {
        bg: calcomColors.bgCancelation,
        text: calcomColors.textCancelation,
        stroke: calcomColors.textCancelation,
    },
    [BookingDataViewType.PENDING]: {
        bg: calcomColors.bgPending,
        text: calcomColors.textPending,
        stroke: 'unset',
    },
};

const IconWrapper = styled.div<{ type: BookingDataViewType }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background-color: ${({ type }) => iconStyles[type]?.bg || 'transparent'};
    margin: 0 auto 16px auto;

    svg {
        width: 32px;
        height: 32px;
        stroke: ${({ type }) => iconStyles[type]?.stroke || 'currentColor'};
        color: ${({ type }) => iconStyles[type]?.text || 'currentColor'};
        stroke-width: 2;
        fill: none;
    }
`;
